
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { 
  CalendarCheck, 
  Users, 
  BarChart3, 
  Mail, 
  Globe, 
  PanelLeft, 
  Shield, 
  Smartphone, 
  CreditCard,
  Megaphone
} from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <div className="border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-all bg-white">
      <div className="h-10 w-10 rounded-full bg-medihub-primary/10 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );
};

const IndustryFeatures = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-2">Platform funkcionalitás</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Minden, amire egy modern eseménykezelő rendszernek szüksége van</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Az EventHub platform minden olyan funkciót tartalmaz, amely segíti a szervezőket a sikeres 
            események lebonyolításában és a résztvevők számára kényelmes felhasználói élményt biztosít.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <FeatureCard 
            icon={<CalendarCheck className="h-5 w-5 text-medihub-primary" />}
            title="Intelligens naptárkezelés"
            description="Könnyen kezelhető, átlátható naptár felület események böngészéséhez, szűréséhez és áttekintéséhez."
          />
          
          <FeatureCard 
            icon={<Users className="h-5 w-5 text-medihub-primary" />}
            title="Résztvevői kezelés"
            description="Egyszerű regisztráció, jelenléti ívek, és résztvevői statisztikák mindössze néhány kattintással."
          />
          
          <FeatureCard 
            icon={<BarChart3 className="h-5 w-5 text-medihub-primary" />}
            title="Részletes analitika"
            description="Pontos adatok az eseményekről, résztvevőkről és interakciókról adatvezérelt döntésekhez."
          />
          
          <FeatureCard 
            icon={<Mail className="h-5 w-5 text-medihub-primary" />}
            title="Automatikus értesítések"
            description="Testreszabható e-mail értesítések az eseményekről, változásokról és emlékeztetőkről."
          />
          
          <FeatureCard 
            icon={<Globe className="h-5 w-5 text-medihub-primary" />}
            title="Többnyelvű támogatás"
            description="Nemzetközi események és többnyelvű közönség kiszolgálása különböző nyelvi beállításokkal."
          />
          
          <FeatureCard 
            icon={<PanelLeft className="h-5 w-5 text-medihub-primary" />}
            title="Szervezői vezérlőpult"
            description="Áttekinthető kezelőfelület az események létrehozásához, szerkesztéséhez és monitorozásához."
          />
          
          <FeatureCard 
            icon={<Shield className="h-5 w-5 text-medihub-primary" />}
            title="Adatvédelem és biztonság"
            description="GDPR-kompatibilis adatkezelés, biztonságos beléptetés és adatvédelmi beállítások."
          />
          
          <FeatureCard 
            icon={<Smartphone className="h-5 w-5 text-medihub-primary" />}
            title="Mobilbarát felület"
            description="Tökéletesen reszponzív felület minden eszközön, mobil és tablet optimalizációval."
          />
          
          <FeatureCard 
            icon={<CreditCard className="h-5 w-5 text-medihub-primary" />}
            title="Fizetési integráció"
            description="Biztonságos online fizetési lehetőségek a fizetős események és jegyértékesítés számára."
          />
          
          <FeatureCard 
            icon={<Megaphone className="h-5 w-5 text-medihub-primary" />}
            title="Marketing eszközök"
            description="Beépített promóciós funkciók, megosztási lehetőségek és közösségi média integráció."
          />
        </div>
      </div>
    </section>
  );
};

export default IndustryFeatures;
